import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import {
  getAuthMessages,
  getRememberedLocale,
  normalizeLocale,
  rememberLocale,
} from "@/lib/authMessages";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const locale = normalizeLocale(params.get("lang") || getRememberedLocale());
  const t = getAuthMessages(locale);
  rememberLocale(locale);

  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr(t.passwordTooShort);
    if (password !== confirm) return setErr(t.passwordMismatch);
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: token.trim(), new_password: password });
      toast.success(t.resetSuccess);
      navigate("/login", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex, locale));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[color:var(--bg-base)]">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-3xl bg-white shadow-xl border border-[color:var(--border-stone)] p-6 sm:p-8 space-y-3"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)] mb-1">
          English Job Interview Simulator
        </div>
        <h1
          className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--text-primary)]"
          data-testid="reset-title"
        >
          {t.resetTitle}
        </h1>
        <p className="text-sm text-[color:var(--text-secondary)]">
          {t.resetSubtitle}
        </p>
        <label className="block">
          <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">
            {t.tokenLabel}
          </span>
          <input
            type="text"
            required
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
            data-testid="reset-token"
          />
        </label>
        <PasswordInput
          label={t.newPassword}
          value={password}
          setValue={setPassword}
          visible={showPassword}
          setVisible={setShowPassword}
          showLabel={t.showPassword}
          hideLabel={t.hidePassword}
          testId="reset-password"
        />
        <PasswordInput
          label={t.confirmNewPassword}
          value={confirm}
          setValue={setConfirm}
          visible={showConfirm}
          setVisible={setShowConfirm}
          showLabel={t.showPassword}
          hideLabel={t.hidePassword}
          testId="reset-confirm"
        />
        {confirm.length > 0 && password !== confirm && (
          <div className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]">
            {t.passwordMismatch}
          </div>
        )}
        {err && (
          <div
            className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]"
            data-testid="reset-error"
          >
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40"
          data-testid="reset-submit"
        >
          {busy ? t.processing : t.resetSubmit}
        </button>
      </form>
    </div>
  );
}

function PasswordInput({ label, value, setValue, visible, setVisible, showLabel, hideLabel, testId }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">
        {label}
      </span>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="new-password"
          className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 pr-20 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-2 text-[11px] font-medium text-[color:var(--brand)]"
          aria-label={visible ? hideLabel : showLabel}
          data-testid={`${testId}-visibility`}
        >
          {visible ? hideLabel : showLabel}
        </button>
      </div>
    </label>
  );
}
